import { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RefreshCw, Download, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { massMigrationApi } from '@/lib/api/massMigration';
import {
  useMassMigrationWizardParsedCsv,
  useMassMigrationWizardIdValidation,
  useMassMigrationWizardIdValidationFailures,
  useMassMigrationWizardExcludedRows,
  useMassMigrationWizardOrgType,
  useMassMigrationWizardActions,
} from '@/stores/massMigrationWizardStore';
import { WizardNavigation } from './WizardNavigation';
import type { OrgIdValidationResult, IdValidationStatus } from '@/types/massMigration';

// ─── Filter pill types ────────────────────────────────────────────────────────

type FilterOption = 'All' | 'Failed' | 'Warnings' | 'Valid';

// ─── PairIndicator ────────────────────────────────────────────────────────────

interface StatusChipProps {
  label: string;
  id: string;
  status: IdValidationStatus | null;
  issue: string | null;
}

function StatusChip({ label, id, status, issue }: StatusChipProps) {
  if (status === null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        {label}: N/A
      </span>
    );
  }

  const colorClass =
    status === 'Valid'
      ? 'border-success/50 bg-success/10 text-success'
      : status === 'Invalid'
        ? 'border-destructive/50 bg-destructive/10 text-destructive'
        : 'border-warning/50 bg-warning/10 text-warning';

  return (
    <span
      title={issue ?? undefined}
      className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs', colorClass)}
    >
      <span className="font-medium">{label}:</span>
      <span className="font-mono">{id.length > 17 ? `${id.slice(0, 8)}…${id.slice(-8)}` : id}</span>
      <span>·</span>
      <span>{status}</span>
    </span>
  );
}

interface PairIndicatorProps {
  result: OrgIdValidationResult;
  showAmplify: boolean;
}

function PairIndicator({ result, showAmplify }: PairIndicatorProps) {
  return (
    <div className="flex flex-wrap gap-1">
      <StatusChip
        label="Bridge"
        id={result.bridgeId}
        status={result.bridgeStatus}
        issue={result.bridgeIssue}
      />
      {showAmplify && (
        <StatusChip
          label="Amplify"
          id={result.amplifyOrgUuid ?? '—'}
          status={result.amplifyStatus}
          issue={result.amplifyIssue}
        />
      )}
    </div>
  );
}

// ─── Stat tile ────────────────────────────────────────────────────────────────

interface StatTileProps {
  label: string;
  value: number;
  variant: 'default' | 'success' | 'destructive' | 'warning' | 'secondary';
}

function StatTile({ label, value, variant }: StatTileProps) {
  const colorMap: Record<StatTileProps['variant'], string> = {
    default: 'text-foreground',
    success: 'text-success',
    destructive: 'text-destructive',
    warning: 'text-warning',
    secondary: 'text-muted-foreground',
  };
  return (
    <div className="flex flex-col items-center rounded-md border border-border bg-muted/30 p-3">
      <span className={cn('text-xl font-bold', colorMap[variant])}>{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ─── Export helpers ───────────────────────────────────────────────────────────

function downloadFailureCsv(results: OrgIdValidationResult[]): void {
  const failed = results.filter(
    (r) => r.bridgeStatus === 'Invalid' || r.amplifyStatus === 'Invalid',
  );
  const header = 'rowNumber,bridgeId,amplifyOrgUuid,bridgeStatus,bridgeIssue,amplifyStatus,amplifyIssue\n';
  const rows = failed
    .map(
      (r) =>
        `${r.rowNumber},${r.bridgeId},${r.amplifyOrgUuid ?? ''},${r.bridgeStatus},"${r.bridgeIssue ?? ''}",${r.amplifyStatus ?? ''},"${r.amplifyIssue ?? ''}"`,
    )
    .join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'id-validation-failures.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── IdValidationStep ─────────────────────────────────────────────────────────

export function IdValidationStep() {
  const parsedCsv = useMassMigrationWizardParsedCsv();
  const idValidationResults = useMassMigrationWizardIdValidation();
  const connectionFailures = useMassMigrationWizardIdValidationFailures();
  const excludedRows = useMassMigrationWizardExcludedRows();
  const orgType = useMassMigrationWizardOrgType();
  const { setIdValidationResults, toggleExcluded, excludeAllFailed, setStep } =
    useMassMigrationWizardActions();

  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterOption>('All');

  const showAmplify = orgType === 'Existing';

  // Build the rows payload for the query
  const rows = useMemo(
    () =>
      (parsedCsv?.rows ?? [])
        .filter((r) => !excludedRows.includes(r.rowNumber))
        .map((r) => ({
          rowNumber: r.rowNumber,
          bridgeId: r.bridgeId,
          amplifyOrgUuid: r.amplifyOrgUuid ?? undefined,
        })),
    [parsedCsv, excludedRows],
  );

  const bridgeIds = useMemo(() => rows.map((r) => r.bridgeId), [rows]);
  const resolvedOrgType = orgType ?? 'New';

  const validateQueryKey = ['mass-migration', 'validate-ids', ...bridgeIds, resolvedOrgType] as const;

  const validateQuery = useQuery({
    queryKey: validateQueryKey,
    queryFn: () => massMigrationApi.validateIds(rows, resolvedOrgType),
    enabled: idValidationResults.length === 0 && rows.length > 0,
    staleTime: Infinity,
  });

  // Sync query result into store (acceptable effect — store write, not fetch trigger)
  useEffect(() => {
    if (validateQuery.data !== undefined) {
      setIdValidationResults(
        validateQuery.data.results,
        validateQuery.data.connectionFailures ?? [],
      );
    }
  }, [validateQuery.data, setIdValidationResults]);

  // Re-run: clear store results then reset (not invalidate) the query. Invalidate
  // keeps the cached data, and if the refetch returns a structurally identical
  // result TanStack hands back the same reference, the sync effect above never
  // fires, and the step renders blank. Reset drops the data so the effect sees a
  // fresh object even when nothing changed.
  const handleRerun = useCallback(() => {
    setIdValidationResults([]);
    void queryClient.resetQueries({ queryKey: ['mass-migration', 'validate-ids'] });
  }, [queryClient, setIdValidationResults]);

  // O(1) row lookup map — Fix 5
  const rowMap = useMemo(
    () => new Map((parsedCsv?.rows ?? []).map((r) => [r.rowNumber, r])),
    [parsedCsv?.rows],
  );

  // Compute stats
  const activeResults = idValidationResults.filter(
    (r) => !excludedRows.includes(r.rowNumber),
  );
  const validCount = activeResults.filter((r) => r.bridgeStatus === 'Valid').length;
  const failedCount = activeResults.filter((r) => r.bridgeStatus === 'Invalid').length;
  const warningCount = activeResults.filter((r) => r.bridgeStatus === 'Warning').length;
  const excludedCount = excludedRows.length;

  const activeFailures = activeResults.filter((r) => r.bridgeStatus === 'Invalid').length;

  // Filtered rows for table
  const filteredResults = idValidationResults.filter((r) => {
    switch (filter) {
      case 'Failed':
        return r.bridgeStatus === 'Invalid';
      case 'Warnings':
        return r.bridgeStatus === 'Warning';
      case 'Valid':
        return r.bridgeStatus === 'Valid';
      default:
        return true;
    }
  });

  const filters: FilterOption[] = ['All', 'Failed', 'Warnings', 'Valid'];

  const isLoading = validateQuery.isFetching;
  const hasResults = idValidationResults.length > 0;

  // Name the system in the title only when every failure came from the same one.
  const failingSystems = new Set(connectionFailures.map((f) => f.system));
  const soleFailingSystem =
    failingSystems.size === 1 ? [...failingSystems][0] : undefined;

  return (
    <div className="space-y-6">
      {/* Stat tiles */}
      {hasResults && (
        <div className="grid grid-cols-4 gap-3">
          <StatTile label="Valid" value={validCount} variant="success" />
          <StatTile label="Failed" value={failedCount} variant="destructive" />
          <StatTile label="Warnings" value={warningCount} variant="warning" />
          <StatTile label="Excluded" value={excludedCount} variant="secondary" />
        </div>
      )}

      {/* Connection failure banner — the rows below say only "lookup failed"; this is why.
          Shown above the row-count alert because a rejected key makes every row's
          "invalid ID" a red herring. */}
      {connectionFailures.length > 0 && !isLoading && (
        <Alert variant="destructive">
          <WifiOff className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>
            {soleFailingSystem !== undefined
              ? `${soleFailingSystem} could not validate these rows`
              : 'Source systems could not validate these rows'}
          </AlertTitle>
          <AlertDescription className="mt-2 space-y-2">
            <ul className="space-y-1" aria-label="Connection failures">
              {connectionFailures.map((failure) => (
                <li key={`${failure.system}-${failure.reason}`} className="text-sm">
                  <span className="font-medium">{failure.system}: </span>
                  {failure.reason} — affected {failure.affectedRows}{' '}
                  {failure.affectedRows === 1 ? 'row' : 'rows'}.
                </li>
              ))}
            </ul>
            <p className="text-sm">
              These rows failed because the lookup could not be completed, not
              necessarily because the IDs are wrong. Check the connection settings and
              API keys for the systems above, then re-run validation.
            </p>
            <Button
              variant="outline"
              size="sm"
              type="button"
              className="shrink-0"
              onClick={handleRerun}
            >
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Re-run validation
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Blocking alert */}
      {activeFailures > 0 && !isLoading && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{activeFailures} rows have invalid IDs</AlertTitle>
          <AlertDescription className="flex items-center gap-3 mt-2">
            <span className="text-sm">Exclude failures or fix the source data before continuing.</span>
            <Button
              variant="outline"
              size="sm"
              type="button"
              className="shrink-0"
              onClick={excludeAllFailed}
            >
              Exclude all failures
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border p-12 text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Validating IDs…</p>
        </div>
      )}

      {/* Error from query */}
      {validateQuery.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Validation failed</AlertTitle>
          <AlertDescription>
            {validateQuery.error instanceof Error
              ? validateQuery.error.message
              : 'An unexpected error occurred.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Filter pills + table */}
      {hasResults && !isLoading && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    filter === f
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/50',
                  )}
                  aria-pressed={filter === f}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => downloadFailureCsv(idValidationResults)}
                disabled={failedCount === 0}
              >
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                Export failures
              </Button>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={handleRerun}
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Re-run validation
              </Button>
            </div>
          </div>

          {/* Row table */}
          <div className="relative rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    IDs
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    Org Name
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r) => {
                  const isExcluded = excludedRows.includes(r.rowNumber);
                  const canExclude = r.bridgeStatus === 'Invalid' && !isExcluded;
                  return (
                    <tr
                      key={r.rowNumber}
                      className={cn(
                        'border-t border-border',
                        isExcluded ? 'opacity-50' : 'bg-background',
                      )}
                    >
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.rowNumber}
                      </td>
                      <td className="px-3 py-2">
                        <PairIndicator result={r} showAmplify={showAmplify} />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {rowMap.get(r.rowNumber)?.orgName ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isExcluded ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={() => toggleExcluded(r.rowNumber)}
                            className="text-xs"
                          >
                            Re-include
                          </Button>
                        ) : canExclude ? (
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() => toggleExcluded(r.rowNumber)}
                            className="border-destructive text-destructive hover:bg-destructive/10 text-xs"
                          >
                            Exclude
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {filteredResults.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-8 text-center text-sm text-muted-foreground"
                    >
                      No rows match the selected filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <WizardNavigation
        onBack={() => setStep(1)}
        onNext={activeFailures === 0 && hasResults ? () => setStep(3) : undefined}
        nextDisabled={activeFailures > 0 || !hasResults}
        nextLabel="Select Modules"
      />
    </div>
  );
}
